/**
 * Framework-agnostic semantic color tokens.
 * Each unique hex/rgba value is defined once in `raw`, then referenced throughout `palette`.
 */

const raw = {
  // Brand — primary (#124E5D consolidated)
  primaryMain: "#124E5D",
  primaryMainLower: "#124e5d",
  primaryLight: "#D0DCDF",
  primaryDark: "#1e434a",
  primaryTealLight: "#E7EDEF",
  primaryTealLightLower: "#e7edef",
  primaryTeal10: "#2A606D",
  primaryContrast: "#FEFEFE",
  primaryStaging: "#113D5C",
  primaryDevelop: "#5B115C",

  // Brand — secondary (#FFBD55 / #FEBD55 — distinct legacy values preserved)
  secondaryMain: "#FFBD55",
  secondaryMustard: "#FEBD55",
  secondaryMustardLower: "#febd55",
  secondaryLight: "#FEC466",
  secondaryDark: "#e7ac4d",
  secondaryContrast: "#FFFFFF",

  // Neutral scale
  neutral50: "#F7F9FA",
  neutral100: "#f5f5f5",
  neutral200: "#eeeeee",
  neutral300: "#e6e6e6",
  pendingBgLegacy: "#E6E6E6",
  neutral400: "#c6c6c6",
  neutral500: "#b8b8b8",
  neutral600: "#979797",
  neutral700: "#6B6B6B",
  neutral800: "#6C6C6C",
  neutral800Lower: "#6c6c6c",
  neutral900: "#2c2c2c",

  // Semantic — success (#1C8E64 consolidated as dark)
  successMain: "#00AC6A",
  successDark: "#1C8E64",
  successLight: "#E8F4F0",
  successContrast: "#FFFFFF",
  pineGreen: "#006C4A",
  pineGreenLight: "#E6F0ED",
  jungleGreen: "#08956A",
  mintGreen: "#67FFAF",
  matchingRoute: "#03AA46",
  greenDark: "#209612",

  // Semantic — warning
  warningMain: "#FFB236",
  warningDark: "#CB9744",
  warningLight: "#F5EADA",
  warningContrast: "#462F00",
  warningSubdued: "#F2EEE6",
  warningAlt: "#a54800",
  alertOrange: "#A54800",
  lightBackground: "#FFFBEB",
  butter: "#FFD977",
  russetBrown: "#803000",
  rust: "#A02B00",
  brandOrangeBrown: "#CB9744",
  lightBeige: "#F5EADA",
  burntOrange: "#FF8964",
  vibrantOrange: "#F97316",
  saffronYellow: "#FBBF24",
  mustardYellow: "#fcd34e",
  amber600: "#da7b12",
  champagne: "#fff3c7",

  // Semantic — error (#EF472F consolidated)
  errorMain: "#EF472F",
  errorDark: "#A02B00",
  errorLight: "#FFEEE9",
  errorContrast: "#FFFFFF",
  tangerineError: "#FF6838",
  lightPeachBrand: "#FFEEE9",
  lightred: "#fe938b",
  fireEngineRed: "#DB2527",
  fireEngineRedLight: "#F5B2B3",
  fireEngineRedLighter: "#FDE4E5",
  fireEngineRedDark: "#A01B1D",
  cancelledText: "#F54100",
  cancelledHoverText: "#D73900",

  // Semantic — info
  infoMain: "#2CA8FF",
  infoDark: "#19548f",
  infoLight: "#E8F0FE",
  infoContrast: "#FFFFFF",
  royalBlue: "#2563EB",
  blue700: "#1d4ed8",
  lavenderBlue: "#DCEAFE",
  confirmedText: "#5C50B2",
  infoBannerButtonHover: "#423CAA",

  // Background
  backgroundDefault: "#FFF",
  backgroundPaper: "#fefefe",
  backgroundSubdued: "#F3F6F7",
  offWhiteLight: "#fbfbfb",
  whiteSmoke: "#EAEEF2",
  blueChalk: "#F7F9FA",
  strokeLowEmphasis: "#F7F9FA",

  // Text
  textPrimary: "#2c2c2c",
  textSecondary: "#6C6C6C",
  textDisabled: "#979797",
  textHint: "#b8b8b8",
  textOnDark: "#fefefe",
  textDeep: "#111B34",
  textBlack: "#222222",
  textGray900: "#1A1C21",
  textGray600: "#5E6470",
  textMidnight: "#082127",
  textDefault: "#000",

  // Status — order
  statusDraftBg: "#E7E7E7",
  statusDraftText: "#6B6B6B",
  statusWaitingBg: "#FFFBEB",
  statusWaitingText: "#A54800",
  statusConfirmedBg: "#F2F1F9",
  statusConfirmedText: "#5C50B2",
  statusDeliveryBg: "#FFF9EE",
  statusDeliveryText: "#FEBD55",
  statusCancelledBg: "#FFF0EB",
  statusDeliveredBg: "#E6F0ED",
  statusDeliveredText: "#006C4A",

  // Chart / data viz (10-color palette)
  chart1: "#222222",
  chart2: "#19548f",
  chart3: "#6c6c6c",
  chart4: "#c6c6c6",
  chart5: "#639faa",
  chart6: "#fe938b",
  chart7: "#4d7a85",
  chart8: "#febd55",
  chart9: "#ff5722",
  chart10: "#bb7ab4",

  // Divider & overlay
  divider: "#E5E7EB",
  dividerAlt: "#b8b8b8",
  overlayDark: "rgba(52, 53, 53, 0.5)",
  overlayLight: "rgba(150, 150, 150, 0.1)",

  // Banner
  bannerWarningBg: "#FFF4E5",
  bannerWarningText: "#070606ff",
  bannerWarningButton: "#FFDD99",
  bannerWarningButtonHover: "#FFCC66",
  bannerInfoBg: "#E8F0FE",
  bannerInfoText: "#5C50B2",
  bannerInfoButton: "#5C50B2",
  bannerInfoButtonHover: "#423CAA",

  // Extended / decorative (legacy names preserved via shim)
  cancel: "#CCC",
  silver: "#ccc",
  teal: "#4d7a85",
  tealLight: "#608892",
  tealMuted: "#c4d3d6",
  darkTeal: "#71959e",
  mediumTeal: "#88a6ae",
  tealLight2: "#e6eced",
  bluegray: "#87a5ad",
  lightbluegray: "#c3d2d6",
  lightblue: "#639faa",
  steelBlue: "#92AEB4",
  palePink: "#CFD9E0",
  lightpurple: "#bb7ab4",
  purplePzazz: "#FB53D8",
  purpleTransparent: "rgba(251, 83, 216, 0.1)",
  whiteCoffee: "#E6D7D2",
  brightOrange: "rgba(254, 189, 85)",
  paleYellowOrange: "rgba(254, 189, 85, 0.1)",
  mustard80: "#FFF2DD",
  mustard90: "#FFF8EE",
  veryLightGray: "#FEFEFE",
} as const;

export const palette = {
  brand: {
    primary: {
      main: raw.primaryMain,
      light: raw.primaryLight,
      dark: raw.primaryDark,
      contrast: raw.primaryContrast,
    },
    secondary: {
      main: raw.secondaryMain,
      light: raw.secondaryLight,
      dark: raw.secondaryDark,
      contrast: raw.secondaryContrast,
    },
  },
  neutral: {
    50: raw.neutral50,
    100: raw.neutral100,
    200: raw.neutral200,
    300: raw.neutral300,
    400: raw.neutral400,
    500: raw.neutral500,
    600: raw.neutral600,
    700: raw.neutral700,
    800: raw.neutral800,
    900: raw.neutral900,
  },
  semantic: {
    success: {
      main: raw.successMain,
      light: raw.successLight,
      dark: raw.successDark,
      contrast: raw.successContrast,
    },
    warning: {
      main: raw.warningMain,
      light: raw.warningLight,
      dark: raw.warningDark,
      contrast: raw.warningContrast,
    },
    error: {
      main: raw.errorMain,
      light: raw.errorLight,
      dark: raw.errorDark,
      contrast: raw.errorContrast,
    },
    info: {
      main: raw.infoMain,
      light: raw.infoLight,
      dark: raw.infoDark,
      contrast: raw.infoContrast,
    },
  },
  background: {
    default: raw.backgroundDefault,
    paper: raw.backgroundPaper,
    subdued: raw.backgroundSubdued,
  },
  text: {
    primary: raw.textPrimary,
    secondary: raw.textSecondary,
    disabled: raw.textDisabled,
    hint: raw.textHint,
  },
  status: {
    done: { bg: raw.successLight, text: raw.successDark },
    pending: { bg: raw.pendingBgLegacy, text: raw.primaryContrast },
    warning: { bg: raw.warningLight, text: raw.warningDark },
    failed: { bg: raw.errorLight, text: raw.tangerineError },
    inProgress: { bg: raw.primaryTealLight, text: raw.primaryMain },
    draft: { bg: raw.statusDraftBg, text: raw.statusDraftText },
    waiting: { bg: raw.statusWaitingBg, text: raw.statusWaitingText },
    confirmed: { bg: raw.statusConfirmedBg, text: raw.statusConfirmedText },
    delivery: { bg: raw.statusDeliveryBg, text: raw.statusDeliveryText },
    cancelled: { bg: raw.statusCancelledBg, text: raw.cancelledText },
    delivered: { bg: raw.statusDeliveredBg, text: raw.statusDeliveredText },
  },
  chart: [
    raw.chart1,
    raw.chart2,
    raw.chart3,
    raw.chart4,
    raw.chart5,
    raw.chart6,
    raw.chart7,
    raw.chart8,
    raw.chart9,
    raw.chart10,
  ],
  divider: raw.divider,
  border: {
    default: raw.divider,
    strong: raw.dividerAlt,
    subtle: raw.neutral200,
    focus: raw.infoMain,
    error: raw.errorMain,
  },
  icon: {
    primary: raw.textPrimary,
    secondary: raw.textSecondary,
    disabled: raw.textDisabled,
    onDark: raw.textOnDark,
    onBrand: raw.primaryContrast,
    inactive: "rgba(0, 0, 0, 0.26)",
    inactiveOnDark: "rgba(255, 255, 255, 0.3)",
  },
  state: {
    hover: {
      bg: raw.neutral100,
      border: raw.neutral200,
    },
    active: {
      bg: raw.primaryTealLight,
      border: raw.primaryMain,
    },
    focus: {
      ring: raw.infoMain,
      border: raw.infoMain,
    },
    disabled: {
      bg: raw.neutral100,
      border: raw.neutral200,
      text: raw.textDisabled,
    },
    selected: {
      bg: raw.primaryTealLight,
      border: raw.primaryMain,
      text: raw.primaryMain,
    },
  },
  alpha: {
    overlayDark: raw.overlayDark,
    overlayLight: raw.overlayLight,
    disabled: 0.38,
    muted: 0.6,
    subtle: 0.12,
    scrim: 0.5,
  },
  banner: {
    warning: {
      bg: raw.bannerWarningBg,
      text: raw.bannerWarningText,
      button: raw.bannerWarningButton,
      buttonHover: raw.bannerWarningButtonHover,
    },
    info: {
      bg: raw.bannerInfoBg,
      text: raw.bannerInfoText,
      button: raw.bannerInfoButton,
      buttonHover: raw.bannerInfoButtonHover,
    },
  },
  overlay: {
    dark: raw.overlayDark,
    light: raw.overlayLight,
  },
  /** Legacy and decorative colors — use semantic tokens for new code */
  extended: {
    primaryProd: raw.primaryMain,
    primaryStaging: raw.primaryStaging,
    primaryDevelop: raw.primaryDevelop,
    primaryTealLight: raw.primaryTealLight,
    tealLightLegacy: raw.primaryTealLightLower,
    primaryTeal10: raw.primaryTeal10,
    secondaryMustard: raw.secondaryMustard,
    cancel: raw.cancel,
    default: raw.textDefault,
    darkSeaGreen: raw.successDark,
    tangerineError: raw.tangerineError,
    darkGoldenrod: raw.warningDark,
    darkteal: raw.primaryDark,
    lightred: raw.lightred,
    blue: raw.infoDark,
    yellow: raw.secondaryMustardLower,
    darkgray: raw.neutral800Lower,
    lightpurple: raw.lightpurple,
    purplePzazz: raw.purplePzazz,
    purpleTransparent: raw.purpleTransparent,
    lightblue: raw.lightblue,
    lightgray: raw.neutral400,
    orange: raw.chart9,
    teal: raw.teal,
    tealLight: raw.tealLight,
    bluegray: raw.bluegray,
    lightbluegray: raw.lightbluegray,
    black: raw.textBlack,
    red: raw.errorMain,
    deepblue: raw.textDeep,
    background: raw.backgroundSubdued,
    greyLight: raw.neutral300,
    greyMediumLight: raw.neutral200,
    greyMedium: raw.neutral400,
    greyDark: raw.neutral800,
    greyBorder: raw.dividerAlt,
    offWhite: raw.neutral100,
    offWhiteLight: raw.offWhiteLight,
    whiteSmoke: raw.whiteSmoke,
    blueChalk: raw.blueChalk,
    white: raw.backgroundPaper,
    green: raw.primaryMainLower,
    greenDark: raw.greenDark,
    greyState: raw.neutral700,
    burntOrange: raw.burntOrange,
    lightPeachBrand: raw.lightPeachBrand,
    midnightBlue: raw.textMidnight,
    lightBeige: raw.lightBeige,
    brandOrangeBrown: raw.brandOrangeBrown,
    palePink: raw.palePink,
    yellowSecondary: raw.secondaryLight,
    primaryTint: raw.primaryLight,
    tealMuted: raw.tealMuted,
    darkTeal: raw.darkTeal,
    mediumTeal: raw.mediumTeal,
    tealLight2: raw.tealLight2,
    silver: raw.silver,
    fordGray: raw.neutral600,
    steelBlue: raw.steelBlue,
    brownishGrey: raw.neutral800Lower,
    whiteCoffee: raw.whiteCoffee,
    brightOrange: raw.brightOrange,
    paleYellowOrange: raw.paleYellowOrange,
    paleGray: raw.overlayLight,
    mustard: raw.secondaryMustard,
    mustardDark: raw.secondaryDark,
    mustard80: raw.mustard80,
    mustard90: raw.mustard90,
    euclayptus: raw.successDark,
    euclayptus80: raw.successLight,
    veryLightGray: raw.veryLightGray,
    rust: raw.rust,
    butter: raw.butter,
    russetBrown: raw.russetBrown,
    gray900: raw.textGray900,
    gray600: raw.textGray600,
    lightGrayBorder: raw.divider,
    darkGraySemiTransparent: raw.overlayDark,
    strokeLowEmphasis: raw.strokeLowEmphasis,
    warningText2: raw.warningContrast,
    warningText3: raw.warningAlt,
    warningColorSubdued: raw.warningSubdued,
    cancelledHoverText: raw.cancelledHoverText,
    matchingRoute: raw.matchingRoute,
    alertOrange: raw.alertOrange,
    lightBackground: raw.lightBackground,
    pineGreen: raw.pineGreen,
    pineGreenLight: raw.pineGreenLight,
    fireEngineRed: raw.fireEngineRed,
    fireEngineRedLight: raw.fireEngineRedLight,
    fireEngineRedLighter: raw.fireEngineRedLighter,
    fireEngineRedDark: raw.fireEngineRedDark,
    vibrantOrange: raw.vibrantOrange,
    saffronYellow: raw.saffronYellow,
    royalBlue: raw.royalBlue,
    jungleGreen: raw.jungleGreen,
    mintGreen: raw.mintGreen,
    lavenderBlue: raw.lavenderBlue,
    blue700: raw.blue700,
    champagne: raw.champagne,
    mustardYellow: raw.mustardYellow,
    amber600: raw.amber600,
    primaryStatusBg: raw.primaryMain,
    primaryDefaultTxt: raw.primaryContrast,
  },
} as const;

export type TPalette = typeof palette;

/** @internal Exported for shim/runtime env logic only */
export const runtimePrimaryColors = {
  prod: raw.primaryMain,
  staging: raw.primaryStaging,
  develop: raw.primaryDevelop,
} as const;

/** @internal Predefined RGB palette for legacy dashboard color pickers */
export const predefinedColors = [
  "rgb(255,0,0)",
  "rgb(0,255,0)",
  "rgb(0,0,255)",
  "rgb(255,255,0)",
  "rgb(0,255,255)",
  "rgb(255,0,255)",
  "rgb(192,192,192)",
  "rgb(128,0,0)",
  "rgb(128,128,0)",
  "rgb(0,128,0)",
  "rgb(128,0,128)",
  "rgb(0,128,128)",
  "rgb(0,0,128)",
  "rgb(70,130,180)",
  "rgb(220,20,60)",
] as const;
